import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
} from "@mui/material";
import { Error as ErrorIcon, Close as CloseIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { 
  setShowPaymentErrorDialog, 
  setShowSubscriptionPlanDialog,
  setPaymentErrorMessage 
} from "src/redux/slices/AuthSlice";
import NameLogo from "./icons/Name-Logo";

const PaymentErrorDialog = () => {
  const dispatch = useDispatch();
  const showPaymentErrorDialog = useSelector(
    (state) => state.auth.showPaymentErrorDialog
  );
  
  // Get error message and businessId from Redux state
  const errorMessage = useSelector(
    (state) => state.auth.paymentErrorMessage || "payment was cancelled or failed. please try again."
  );
  
  const lastBusinessId = useSelector(
    (state) => state.auth.lastSubscriptionBusinessId
  );

  const handleClose = () => {
    dispatch(setShowPaymentErrorDialog(false));
    dispatch(setPaymentErrorMessage(null));
  };

  const handleRetry = () => {
    // Close error dialog
    dispatch(setShowPaymentErrorDialog(false));
    dispatch(setPaymentErrorMessage(null));
    
    // Reopen subscription dialog with the stored businessId
    if (lastBusinessId) {
      dispatch(setShowSubscriptionPlanDialog({ 
        show: true, 
        businessId: lastBusinessId,
        currentPlan: null
      }));
    } else {
      // If no businessId stored, just open the dialog
      dispatch(setShowSubscriptionPlanDialog({ 
        show: true, 
        businessId: null,
        currentPlan: null
      }));
    }
  };

  return (
    <Dialog
      open={showPaymentErrorDialog}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative" }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            p: 4,
            pt: 5,
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 2 }}>
            <Avatar
              src="/logo.svg"
              sx={{
                width: 90,
                height: 90,
                bgcolor: "#fff",
                border: "4px solid #A259FF",
                mx: "auto",
                padding: 5,
              }}
            >
              <NameLogo />
            </Avatar>
          </Box>

          {/* Brand Name */}
          <Typography
            variant="h3"
            align="center"
            fontWeight="bold"
            letterSpacing={1}
            sx={{
              mb: 0.5,
              textTransform: "lowercase",
              color: "#000",
              fontFamily: "Inter, Arial, sans-serif",
            }}
          >
            be
            <Box component="span" sx={{ color: "#A259FF" }}>
              ɘ
            </Box>
            mz
          </Typography>

          {/* Tagline */}
          <Typography
            variant="body1"
            fontWeight="bold"
            color="#000"
            sx={{ mb: 3 }}
          >
            the out & about chat app
          </Typography>

          {/* Error Icon */}
          <ErrorIcon
            sx={{
              fontSize: 80,
              color: "#f44336",
              mb: 2,
            }}
          />

          {/* Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mb: 2, color: "#000", textTransform: "lowercase" }}
          >
            payment failed
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: "400px", textTransform: "lowercase" }}
          >
            {errorMessage}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%", maxWidth: "400px" }}>
            {/* Retry Button */}
            <Button
              variant="contained"
              size="large"
              onClick={handleRetry}
              sx={{
                backgroundColor: "#9347FF",
                color: "#FFFFFF",
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "lowercase",
                fontSize: "1rem",
                fontWeight: "bold",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  backgroundColor: "#8938e9",
                  boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              retry
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentErrorDialog;

